import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Server, Lock, FolderOpen, ShieldOff, Building2, Loader2, RefreshCw, Inbox, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  apiClient,
  getCrmProjects,
  getCrmSessionContext,
  CrmProjectsResponse,
  CrmSsoStatus,
  Project,
} from '../services/api';
import { GlassButton } from './ui/GlassButton';
import CrmProjectBar from './CRM/CrmProjectBar';
import CrmUnassignedPanel from './CRM/CrmUnassignedPanel';
import './CRMWorkspace.css';
import './CRM/CrmProjectBar.css';

/**
 * CRM workspace (full-frame SuiteCRM iframe).
 *
 * crm-per-project-scoping: when GET /api/crm/projects says `scoping: true` the
 * CRM is opened for the project selected in AiMe (`/crm/_sso?project_id=…`) or
 * the caller's Unassigned bucket (`?bucket=unassigned`), keyed by `ssoKey`.
 * Otherwise (flag off, unmigrated user, company user, call failed) it behaves
 * exactly as before: one SSO without a project, ssoKey = 'legacy', no bar, and
 * a sidebar project switch never re-runs SSO.
 */
export interface CRMWorkspaceProps {
  selectedProjectId?: string;
  projects?: Project[];
  onProjectSelect?: (id: string | undefined) => void;
  /** Whether /leads is currently shown. Hidden pre-mount SSOs once; later key
   *  changes while hidden only mark the iframe stale until it becomes visible. */
  isVisible?: boolean;
}

const LEGACY_KEY = 'legacy';
const BUCKET_STORAGE = 'aime_crm_bucket';
const SSO_KEY_STORAGE = 'aime_crm_sso_key';
const CHANNEL = 'aime-crm';
// SuiteCRM's PHP session is purged after 24 min idle (session.gc_maxlifetime=1440 on the CRM box),
// and a hidden iframe never re-runs SSO. Ping /crm/session-status well inside that window to keep the
// session alive; an `active:false` answer means it already died, so we re-run SSO and reload the iframe.
const KEEPALIVE_MS = 10 * 60 * 1000;
const KEEPALIVE_MIN_GAP_MS = 30 * 1000;   // focus/visibility bursts never ping more than this often

type RefusalStatus = Exclude<CrmSsoStatus, 'no-credentials'>;

const REFUSAL_CARDS: Record<RefusalStatus, { title: string; text: string }> = {
  'no-module': { title: 'CRM not available', text: "CRM isn't included in your plan." },
  'project-required': { title: 'Choose a project', text: 'Select a project to open its CRM.' },
  'no-project-access': { title: 'No access', text: "You don't have access to this project's CRM." },
  'project-not-ready': { title: 'Almost ready', text: 'Setting up CRM access for this project…' },
  'project-company-crm': { title: 'Company CRM', text: "This project's CRM is managed in its company CRM." },
};

const REFUSAL_ICONS: Record<RefusalStatus, React.ReactNode> = {
  'no-module': <Lock size={48} color="var(--text-secondary)" style={{ marginBottom: 16 }} />,
  'project-required': <FolderOpen size={48} color="var(--text-secondary)" style={{ marginBottom: 16 }} />,
  'no-project-access': <ShieldOff size={48} color="var(--text-secondary)" style={{ marginBottom: 16 }} />,
  'project-not-ready': <Loader2 size={48} className="crm-spin" color="var(--text-secondary)" style={{ marginBottom: 16 }} />,
  'project-company-crm': <Building2 size={48} color="var(--text-secondary)" style={{ marginBottom: 16 }} />,
};

const readBucket = (): 'project' | 'unassigned' => {
  try {
    return sessionStorage.getItem(BUCKET_STORAGE) === 'unassigned' ? 'unassigned' : 'project';
  } catch {
    return 'project';
  }
};

/** Short stable hash for the iframe cache-buster (`/crm/?_p=<h>`). */
export const keyHash = (key: string): string => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (Math.imul(31, h) + key.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};

export const computeSsoKey = (
  scoping: boolean,
  selectedProjectId: string | undefined,
  bucket: 'project' | 'unassigned',
): string => (scoping ? `${bucket === 'unassigned' ? '' : selectedProjectId || ''}|${bucket}` : LEGACY_KEY);

const cardStyle: React.CSSProperties = {
  background: 'var(--panel)',
  border: '1px solid var(--stroke)',
  borderRadius: 16,
  padding: 40,
  maxWidth: 480,
  textAlign: 'center',
  backdropFilter: 'blur(20px)',
};

const centerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 32,
};

const CRMWorkspace: React.FC<CRMWorkspaceProps> = ({
  selectedProjectId, onProjectSelect, isVisible = true,
}) => {
  const { user } = useAuth();
  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [crmConfigured, setCrmConfigured] = useState<boolean | null>(null);
  // Set when /crm/_sso reports the user has no CRM account of their own
  // (backend fails closed — never the shared admin account).
  const [noCredentials, setNoCredentials] = useState(false);
  const [refusal, setRefusal] = useState<RefusalStatus | null>(null);
  // The key whose SSO last answered `project-not-ready`; drives the "Try again" button.
  const [notReadyKey, setNotReadyKey] = useState<string | null>(null);
  // The key whose SSO produced the CURRENT iframe `src` (iframe + bar follow this, not the
  // pending selection, so a hidden/stale iframe never reloads on a sidebar click).
  const [iframeKey, setIframeKey] = useState<string | null>(null);

  // crm-per-project-scoping state
  const [crmInfo, setCrmInfo] = useState<CrmProjectsResponse | null>(null);
  const [infoLoaded, setInfoLoaded] = useState(false);
  const [bucket, setBucket] = useState<'project' | 'unassigned'>(readBucket);
  const [switchedTo, setSwitchedTo] = useState<string | null>(null);
  const [unassignedOpen, setUnassignedOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  // Bumped after a keep-alive recovery so the iframe remounts even though its src is unchanged.
  const [reloadNonce, setReloadNonce] = useState(0);

  const scopingRef = useRef(false);
  const scoping = !!crmInfo?.scoping;
  scopingRef.current = scoping;
  const ssoKey = computeSsoKey(scoping, selectedProjectId, bucket);
  const lastKeyRef = useRef<string | null>(null);   // key of the last SSO started
  const inFlight = useRef(false);
  const directMode = useRef(false);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // BLOCKER-005: the one-shot not-ready auto-retry is tracked PER KEY in a ref (a state
  // flag captured in a stale closure left a freshly-shared project stuck on the card).
  const retriedKeyRef = useRef<string | null>(null);
  const wasNotReadyRef = useRef(false);
  const currentKeyRef = useRef(ssoKey);
  currentKeyRef.current = ssoKey;
  const lastPingRef = useRef(0);
  const pingingRef = useRef(false);
  // What the iframe is actually logged in as (not the pending selection) and whether it can be pinged.
  const loadedKeyRef = useRef<string | null>(null);
  loadedKeyRef.current = iframeKey;

  // 1. Is project scoping active for this user? (Company users keep today's path.)
  useEffect(() => {
    let cancelled = false;
    if (user?.company_id) {
      setInfoLoaded(true);
      return;
    }
    getCrmProjects()
      .then(data => { if (!cancelled) setCrmInfo(data); })
      .catch(() => { if (!cancelled) setCrmInfo(null); })   // fail closed for the claim: no bar, legacy SSO
      .finally(() => { if (!cancelled) setInfoLoaded(true); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const projectName = useCallback((pid?: string | null): string => {
    if (!pid) return 'another project';
    if (pid === 'unassigned') return 'Unassigned records';
    return crmInfo?.projects.find(p => p.project_id === pid)?.name || 'another project';
  }, [crmInfo]);

  const initProxySSO = useCallback(async (key: string, isScoped: boolean): Promise<boolean> => {
    setRefusal(null);
    setNoCredentials(false);
    setNotReadyKey(null);
    let url = '/crm/_sso';
    if (isScoped) {
      const [pid, b] = key.split('|');
      if (b === 'unassigned') {
        url = '/crm/_sso?bucket=unassigned';
      } else if (pid) {
        url = `/crm/_sso?project_id=${encodeURIComponent(pid)}`;
      } else {
        setIframeSrc(null);
        setIframeKey(null);
        setRefusal('project-required');
        return false;
      }
    }
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      const status = res.headers.get('X-AIME-CRM-Status') as CrmSsoStatus | null;
      if (status === 'no-credentials') {
        setNoCredentials(true);
        return false;
      }
      if (status && status in REFUSAL_CARDS) {
        setIframeSrc(null);
        setIframeKey(null);
        setRefusal(status as RefusalStatus);
        if (status === 'project-not-ready') {
          wasNotReadyRef.current = true;
          setNotReadyKey(key);                       // "Try again" is available straight away
          if (retriedKeyRef.current !== key) {       // ...and ONE automatic retry per key
            retriedKeyRef.current = key;
            const after = Math.min(30, Math.max(1, parseInt(res.headers.get('Retry-After') || '5', 10) || 5));
            if (retryTimer.current) clearTimeout(retryTimer.current);
            retryTimer.current = setTimeout(() => {
              if (currentKeyRef.current !== key) return;   // the user moved on
              lastKeyRef.current = null;                    // force a re-run for the same key
              setRetryTick(t => t + 1);
            }, after * 1000);
          }
        }
        return false;
      }
    } catch (_) {
      // SSO failed — still load the iframe; user can log in manually
    }
    if (isScoped) {
      setIframeSrc(`/crm/?_p=${keyHash(key)}`);
      setIframeKey(key);
      if (wasNotReadyRef.current) {
        // A not-ready project just became ready: refresh the bar's project list.
        wasNotReadyRef.current = false;
        getCrmProjects().then(setCrmInfo).catch(() => {});
      }
      try { sessionStorage.setItem(SSO_KEY_STORAGE, key); } catch { /* ignore */ }
      try { channelRef.current?.postMessage({ type: 'sso', key }); } catch { /* ignore */ }
    } else {
      setIframeSrc('/crm/');
    }
    setSwitchedTo(null);
    return true;
  }, []);

  const initDirectSSO = useCallback(async (key: string, isScoped: boolean) => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch('/crm/_sso_bridge', {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Bridge returned ${res.status}`);
      const data = await res.json();
      if (data.sso_url) {
        sessionStorage.setItem('crm_direct_active', 'true');
        directMode.current = true;
        setIframeSrc(data.sso_url);
        setIframeKey(key);
        return;
      }
    } catch (_) {
      // Bridge failed — fall back to proxy
    }
    await initProxySSO(key, isScoped);
  }, [initProxySSO]);

  const runSSO = useCallback(async (key: string, isScoped: boolean, first: boolean) => {
    if (!first) {
      // Project/bucket switch: company users and direct mode never get here
      // with a changed key (their key is always 'legacy').
      await initProxySSO(key, isScoped);
      return;
    }
    // For enterprise users, check if their company has a CRM instance configured
    if (user?.company_id) {
      try {
        const res = await apiClient.get(`/companies/${user.company_id}/crm-instance/health`);
        if (res.data?.status !== 'active') {
          setCrmConfigured(false);
          return;
        }
      } catch {
        setCrmConfigured(false);
        return;
      }
    }

    setCrmConfigured(true);

    // Resolve CRM mode if not yet cached (race: background pre-fetch
    // in AuthContext may not have completed before user navigates here)
    let mode: string = sessionStorage.getItem('crm_mode') || '';
    if (!mode) {
      try {
        const token = localStorage.getItem('access_token');
        const res = await fetch('/crm/_sso_resolve', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        mode = data.mode || 'proxy';
        sessionStorage.setItem('crm_mode', mode);
        if (data.crm_url) sessionStorage.setItem('crm_url', data.crm_url);
      } catch {
        mode = 'proxy';
      }
    }

    if (mode === 'direct') {
      await initDirectSSO(key, isScoped);
    } else {
      await initProxySSO(key, isScoped);
    }
  }, [user, initDirectSSO, initProxySSO]);

  // 2. (Re-)run SSO whenever the key changes. The first SSO runs even while
  //    hidden (pre-mount); later changes wait until /leads is visible.
  useEffect(() => {
    if (!infoLoaded) return;
    if (lastKeyRef.current === ssoKey) return;
    if (retryTimer.current && lastKeyRef.current !== null) {    // key changed: drop the old key's retry
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
    const first = lastKeyRef.current === null && !iframeSrc && !refusal && !noCredentials;
    if (!first && !isVisible) return;           // stale; re-SSO when visible
    if (directMode.current && !first) return;   // company direct bridge: unchanged
    if (inFlight.current) return;
    lastKeyRef.current = ssoKey;
    inFlight.current = true;
    runSSO(ssoKey, scoping, first).finally(() => {
      inFlight.current = false;
      // The key changed while this SSO was in flight: run again for the new key.
      if (currentKeyRef.current !== lastKeyRef.current) setRetryTick(t => t + 1);
    });
  }, [infoLoaded, ssoKey, isVisible, scoping, runSSO, retryTick]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (retryTimer.current) clearTimeout(retryTimer.current); }, []);

  // 2b. Keep the CRM session alive and recover if it already died. Proxy mode only: the company
  //     direct bridge loads the CRM from its own origin, where /crm/session-status is not ours to call.
  //     Only ever pings after SSO has produced an iframe — an unauthenticated /session-status
  //     destroys the PHP session it was sent on.
  const keepAlive = useCallback(async () => {
    const key = loadedKeyRef.current;
    if (!key && scopingRef.current) return;
    if (directMode.current || pingingRef.current || inFlight.current) return;
    if (Date.now() - lastPingRef.current < KEEPALIVE_MIN_GAP_MS) return;
    pingingRef.current = true;
    lastPingRef.current = Date.now();
    try {
      let dead = false;
      try {
        const res = await fetch('/crm/session-status', { credentials: 'include' });
        if (!res.ok) return;                         // proxy/CRM blip: unknown, not "expired"
        const data = await res.json();
        dead = data?.active === false;               // anything else (incl. malformed) is unknown
      } catch {
        return;                                      // network error: try again next tick
      }
      if (!dead) return;
      // Session is gone: log in again as the same identity the iframe was showing, then remount it.
      inFlight.current = true;
      try {
        const ok = await initProxySSO(key || LEGACY_KEY, scopingRef.current);
        if (ok) setReloadNonce(n => n + 1);
      } finally {
        inFlight.current = false;
      }
    } finally {
      pingingRef.current = false;
    }
  }, [initProxySSO]);

  useEffect(() => {
    if (!iframeSrc) return;
    const timer = setInterval(() => { keepAlive(); }, KEEPALIVE_MS);
    const onVis = () => { if (document.visibilityState === 'visible') keepAlive(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
  }, [iframeSrc, keepAlive]);

  // Arriving on /leads after a long idle: check straight away so the login screen is never shown.
  useEffect(() => {
    if (isVisible && iframeSrc) keepAlive();
  }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  // 3. Two-tab detection: BroadcastChannel + session-context (scoped users only).
  useEffect(() => {
    if (!scoping || typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel(CHANNEL);
    channelRef.current = ch;
    ch.onmessage = (ev: MessageEvent) => {
      const msg = ev.data;
      if (!msg || msg.type !== 'sso' || typeof msg.key !== 'string') return;
      if (msg.key === lastKeyRef.current) return;
      setSwitchedTo(msg.key.split('|')[1] === 'unassigned' ? 'unassigned' : msg.key.split('|')[0]);
    };
    return () => { ch.close(); channelRef.current = null; };
  }, [scoping]);

  useEffect(() => {
    if (!scoping) return;
    const check = async () => {
      if (document.visibilityState !== 'visible' || !iframeSrc) return;
      try {
        const ctx = await getCrmSessionContext();
        if (!ctx || ctx.project_id === null || ctx.project_id === undefined) return;  // unknown → no overlay
        // Compare against what THIS tab last loaded, not the (possibly pending) selection.
        const [loadedPid, loadedBucket] = (iframeKey || '').split('|');
        const mine = loadedBucket === 'unassigned' ? 'unassigned' : loadedPid;
        const theirs = ctx.bucket === 'unassigned' ? 'unassigned' : ctx.project_id;
        if (theirs && mine && theirs !== mine) setSwitchedTo(theirs);
      } catch { /* UX aid only */ }
    };
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', check);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', check);
    };
  }, [scoping, iframeSrc, iframeKey]);

  const rerunSSO = () => {
    setSwitchedTo(null);
    retriedKeyRef.current = currentKeyRef.current;   // a manual retry never also auto-loops
    lastKeyRef.current = null;
    setRetryTick(t => t + 1);
  };

  const selectProjectFromBar = (pid: string) => {
    try { sessionStorage.removeItem(BUCKET_STORAGE); } catch { /* ignore */ }
    setBucket('project');
    onProjectSelect?.(pid);
  };

  const selectUnassigned = () => {
    try { sessionStorage.setItem(BUCKET_STORAGE, 'unassigned'); } catch { /* ignore */ }
    setBucket('unassigned');
  };

  // While a switch is loading the bar keeps naming the CRM actually shown in the iframe.
  const [loadedPid, loadedBucketRaw] = iframeKey && scoping ? iframeKey.split('|') : [undefined, undefined];
  const loadedBucket = loadedBucketRaw === 'unassigned' ? 'unassigned' as const
    : loadedBucketRaw === 'project' ? 'project' as const : undefined;
  const showBar = !!crmInfo && (crmInfo.scoping || crmInfo.support_view);
  const bar = showBar && crmInfo ? (
    <CrmProjectBar
      info={crmInfo}
      selectedProjectId={loadedPid ?? selectedProjectId}
      bucket={loadedBucket ?? bucket}
      onSelectProject={selectProjectFromBar}
      onSelectUnassigned={selectUnassigned}
    />
  ) : null;

  // Enterprise user with no CRM configured
  if (crmConfigured === false) {
    return (
      <div className="crm-workspace-fullframe" style={centerStyle}>
        <div style={cardStyle}>
          <Server size={48} color="var(--text-secondary)" style={{ marginBottom: 16 }} />
          <h3 style={{ color: 'var(--text)', margin: '0 0 8px' }}>CRM Not Configured</h3>
          <p style={{ color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
            Your company's SuiteCRM instance hasn't been set up yet.
            Contact your company administrator to configure the CRM connection.
          </p>
        </div>
      </div>
    );
  }

  // Individual user whose CRM workspace could not be provisioned
  if (noCredentials) {
    return (
      <div className="crm-workspace-fullframe" style={centerStyle}>
        <div style={cardStyle}>
          <Server size={48} color="var(--text-secondary)" style={{ marginBottom: 16 }} />
          <h3 style={{ color: 'var(--text)', margin: '0 0 8px' }}>CRM Workspace Not Ready</h3>
          <p style={{ color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
            We couldn't set up your CRM workspace just now. Please try again in a
            few minutes, or contact support if this keeps happening.
          </p>
        </div>
      </div>
    );
  }

  // crm-per-project-scoping refusals (distinct cards)
  if (refusal) {
    const card = REFUSAL_CARDS[refusal];
    const canRetry = refusal === 'project-not-ready' && notReadyKey === ssoKey;
    return (
      <div className="crm-workspace-fullframe" style={centerStyle}>
        {bar}
        <div style={cardStyle} data-testid={`crm-refusal-${refusal}`}>
          {REFUSAL_ICONS[refusal]}
          <h3 style={{ color: 'var(--text)', margin: '0 0 8px' }}>{card.title}</h3>
          <p style={{ color: 'var(--text-secondary)', margin: canRetry ? '0 0 16px' : 0, lineHeight: 1.6 }}>
            {card.text}
          </p>
          {canRetry && (
            <GlassButton variant="primary" pill icon={<RefreshCw size={16} />} onClick={rerunSSO}>
              Try again
            </GlassButton>
          )}
        </div>
      </div>
    );
  }

  if (!iframeSrc) {
    return (
      <div className="crm-workspace-fullframe" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {bar}
        <div style={{ color: 'var(--text-secondary)' }}>
          Initialising CRM session…
        </div>
      </div>
    );
  }

  const currentLabel = bucket === 'unassigned' ? 'Unassigned records' : projectName(selectedProjectId);
  const frameKey = `${iframeKey ?? ssoKey}#${reloadNonce}`;

  return (
    <div className="crm-workspace-fullframe">
      <div className="crm-iframe-container-fullframe">
        <iframe
          key={frameKey}
          src={iframeSrc}
          title="SuiteCRM"
          className="crm-iframe-fullframe"
          style={{
            width: '100%',
            height: '100vh',
            border: 'none',
            display: 'block',
          }}
        />
      </div>
      {bar}
      {scoping && crmInfo?.unassigned?.available && !bannerDismissed && bucket !== 'unassigned' && (
        <div className="crm-unassigned-banner" role="status">
          <Inbox size={16} />
          <span>Some CRM records aren't assigned to a project yet.</span>
          <GlassButton variant="primary" pill size="sm" onClick={() => setUnassignedOpen(true)}>
            Review
          </GlassButton>
          <button
            type="button"
            className="crm-unassigned-banner__dismiss"
            aria-label="Dismiss"
            onClick={() => setBannerDismissed(true)}
          >
            <X size={14} />
          </button>
        </div>
      )}
      {scoping && switchedTo && (
        <div className="crm-switch-overlay" role="alertdialog" aria-modal="true">
          <div className="crm-switch-overlay__card">
            <h3 style={{ margin: '0 0 8px', color: 'var(--text)' }}>
              CRM switched to {projectName(switchedTo)} in another tab
            </h3>
            <p>This browser has one CRM session, so the other tab changed what this one shows.</p>
            <GlassButton variant="primary" pill icon={<RefreshCw size={16} />} onClick={rerunSSO}>
              Reload for {currentLabel}
            </GlassButton>
          </div>
        </div>
      )}
      {scoping && crmInfo && (
        <CrmUnassignedPanel
          isOpen={unassignedOpen}
          onClose={() => setUnassignedOpen(false)}
          projects={crmInfo.projects}
          onChanged={() => {
            // Refresh the bar's bucket info quietly.
            getCrmProjects().then(setCrmInfo).catch(() => {});
          }}
        />
      )}
    </div>
  );
};

export default CRMWorkspace;
